import { apiFetch } from "./apiClient";

// The real Housekeeping resource — one cleaning job per room, from the moment
// a guest checks out to the moment a supervisor puts the room back on the
// market.
//
// NOT the same thing as the "Housekeeping" CATEGORY on the guest-service queue
// (see guestServiceRequestsApi.ts). That is something a guest ASKED FOR while
// in the room; this is what the hotel owes the room after they have gone, and
// only this one decides whether the room can be sold again.
//
// Nor is it housekeepingStore.ts, which is the demo/session-storage model the
// mobile team app started on.

// ------------------------------------------------------------- Status master

/** One row of the shared status master, category "Housekeeping" — the same
 * rows `GET /statusmasters?category=Housekeeping` returns, handed over in
 * workflow order. */
export interface HousekeepingStatusDto {
  statusId: number;
  /** The stable identifier — PENDING, ASSIGNED, IN_PROGRESS, PAUSED,
   * COMPLETED, REWORK_REQUIRED, CANCELLED. The ONLY field to branch on. */
  statusCode: string;
  /** Display text. Per-account and renameable, so never a condition. */
  statusName: string;
  isDefault: boolean;
  isActive: boolean;
  /** Position in the workflow; -1 for a status this application knows no
   * rules for. */
  workflowOrder: number;
}

/** The status codes the backend's HousekeepingStatus defines, mirrored here so
 * the screen can name them without stringly-typing each call site.
 *
 * CODES, NEVER NAMES: an account may rename "Pending" to "Awaiting pickup"
 * from the Status Masters screen, and every rule on both sides still matches
 * PENDING. */
export const HK_STATUS = {
  Pending: "PENDING",
  Assigned: "ASSIGNED",
  InProgress: "IN_PROGRESS",
  Paused: "PAUSED",
  Completed: "COMPLETED",
  ReworkRequired: "REWORK_REQUIRED",
  Cancelled: "CANCELLED",
} as const;

export type HousekeepingStatusCode =
  (typeof HK_STATUS)[keyof typeof HK_STATUS];

/** Fallback labels for a client rendering before the status master has
 * loaded. The server's own statusName wins whenever it is available — these
 * exist so a chip is never blank, not to override an account's wording. */
export const HK_STATUS_FALLBACK_LABEL: Record<string, string> = {
  [HK_STATUS.Pending]: "Pending",
  [HK_STATUS.Assigned]: "Assigned",
  [HK_STATUS.InProgress]: "In Progress",
  [HK_STATUS.Paused]: "Paused",
  [HK_STATUS.Completed]: "Completed",
  [HK_STATUS.ReworkRequired]: "Rework Required",
  [HK_STATUS.Cancelled]: "Cancelled",
};

// ------------------------------------------------------------------- The job

export interface HousekeepingRequestStatusHistoryDto {
  housekeepingRequestStatusHistoryId: number;
  oldStatusId: number | null;
  oldStatusCode: string | null;
  oldStatusName: string | null;
  newStatusId: number;
  newStatusCode: string | null;
  newStatusName: string | null;
  changedDateTime: string;
  changedBy: number | null;
  /** Whatever the transition carried — the pause reason, the rework reason,
   * the cancellation reason, the cleaner's notes. */
  remarks: string | null;
}

/** Maps to the backend's HousekeepingRequestDto. */
export interface HousekeepingRequestDto {
  housekeepingRequestId: number;
  accountId: number;
  branchId: number | null;

  roomId: number;
  roomNo: string | null;
  roomTypeId: number;
  roomTypeName: string | null;
  buildingId: number | null;
  floorId: number | null;

  /** The PHYSICAL room's status (category "Room": AVAILABLE | OCCUPIED |
   * HOUSEKEEPING | RESERVED | MAINTENANCE).
   *
   * This — not `statusCode` — is what says whether the room can be sold. A job
   * on COMPLETED with the room still HOUSEKEEPING is exactly the state the
   * inspection step exists to resolve. */
  roomStatusCode: string | null;
  roomStatusName: string | null;

  roomBookingId: number | null;
  directBookingId: number | null;
  /** The guest who vacated the room; null on a hand-raised job. */
  guestName: string | null;
  checkedOutOn: string | null;

  /** "Checkout" (opened automatically by check-out) | "Manual". */
  requestSource: string;
  isUrgent: boolean;

  statusId: number;
  /** The JOB's status code. See HK_STATUS. */
  statusCode: string;
  /** Display text for the code above — per-account, so render this and branch
   * on `statusCode`. */
  statusName: string;

  assignedToUserId: number | null;
  assignedToUserName: string | null;
  assignedBy: number | null;
  assignedOn: string | null;

  startedBy: number | null;
  startedOn: string | null;

  pausedBy: number | null;
  pausedOn: string | null;
  pauseReason: string | null;

  completedBy: number | null;
  completedOn: string | null;
  cleaningNotes: string | null;

  inspectedBy: number | null;
  inspectedOn: string | null;
  /** Null until someone inspects; true = passed (the room went AVAILABLE);
   * false = failed into REWORK_REQUIRED. */
  inspectionPassed: boolean | null;
  reworkReason: string | null;
  reworkCount: number;

  cancelledBy: number | null;
  cancelledOn: string | null;
  cancellationReason: string | null;

  notes: string | null;
  isActive: boolean;

  createdBy: number | null;
  createdOn: string;
  modifiedBy: number | null;
  modifiedOn: string | null;

  /** The status codes this job may move to right now, straight from the
   * server's own state machine — so the screen draws exactly the buttons the
   * server would accept instead of re-encoding the machine here and drifting
   * from it. Empty on a cancelled job and on one that has passed. */
  allowedTransitions: string[] | null;

  /** COMPLETED and not yet inspected — the one state that shows an Inspect
   * action. */
  awaitingInspection: boolean;

  statusHistory: HousekeepingRequestStatusHistoryDto[] | null;
}

// ------------------------------------------------------------- Action bodies

export interface CreateHousekeepingRequestDto {
  roomId: number;
  isUrgent: boolean;
  notes: string | null;
  /** Optional: rosters the new job straight to ASSIGNED instead of leaving it
   * PENDING. */
  assignedToUserId: number | null;
  performedBy: number | null;
}

export interface AssignHousekeepingRequestDto {
  assignedToUserId: number;
  remarks: string | null;
  performedBy: number | null;
}

export interface StartHousekeepingRequestDto {
  remarks: string | null;
  performedBy: number | null;
}

export interface PauseHousekeepingRequestDto {
  /** Required by the server. */
  pauseReason: string;
  performedBy: number | null;
}

export interface ResumeHousekeepingRequestDto {
  remarks: string | null;
  performedBy: number | null;
}

export interface CompleteHousekeepingRequestDto {
  cleaningNotes: string | null;
  performedBy: number | null;
}

export interface ApproveInspectionDto {
  remarks: string | null;
  performedBy: number | null;
}

export interface MarkReworkRequiredDto {
  /** Required by the server — the cleaner has to be told what to redo. */
  reworkReason: string;
  performedBy: number | null;
}

export interface CancelHousekeepingRequestDto {
  /** Required by the server. */
  cancellationReason: string;
  performedBy: number | null;
}

export interface HousekeepingListFilters {
  accountId?: number;
  branchId?: number | null;
  roomId?: number;
  roomBookingId?: number;
  /** A status CODE, never a display name. */
  statusCode?: string;
  /** Everything except CANCELLED — including COMPLETED, because a cleaned
   * room is still off the market until it has been inspected. */
  openOnly?: boolean;
  awaitingInspection?: boolean;
  assignedToUserId?: number;
  isUrgent?: boolean;
  fromDate?: string;
  toDate?: string;
  search?: string;
  take?: number;
}

const buildQuery = (params: object): string => {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params as Record<string, unknown>)) {
    if (value !== undefined && value !== null) qs.set(key, String(value));
  }
  const str = qs.toString();
  return str ? `?${str}` : "";
};

// ------------------------------------------------------------------- Reads

export const listHousekeepingRequests = async (
  filters: HousekeepingListFilters = {}
): Promise<HousekeepingRequestDto[]> => {
  const res = await apiFetch(`/housekeeping${buildQuery(filters)}`);
  return res.json();
};

/** One page of the Housekeeping page, filtered and counted by the server. */
export interface HousekeepingPageDto {
  items: HousekeepingRequestDto[];
  total: number;
  page: number;
  pageSize: number;
  pendingCount: number;
  inProgressCount: number;
  awaitingInspectionCount: number;
  reworkCount: number;
}

export const listHousekeepingPage = async (q: {
  accountId?: number;
  branchId?: number | null;
  statusCode?: string;
  includeCancelled?: boolean;
  search?: string;
  page: number;
  pageSize: number;
}): Promise<HousekeepingPageDto> => {
  const res = await apiFetch(
    `/housekeeping/page${buildQuery({
      accountId: q.accountId,
      branchId: q.branchId ?? undefined,
      statusCode: q.statusCode || undefined,
      includeCancelled: q.includeCancelled ? true : undefined,
      search: q.search?.trim() || undefined,
      page: q.page,
      pageSize: q.pageSize,
    })}`
  );
  return res.json();
};

export const getHousekeepingRequest = async (
  id: number,
  accountId?: number
): Promise<HousekeepingRequestDto> => {
  const res = await apiFetch(`/housekeeping/${id}${buildQuery({ accountId })}`);
  return res.json();
};

export const listHousekeepingStatuses = async (params: {
  accountId?: number;
  branchId?: number | null;
} = {}): Promise<HousekeepingStatusDto[]> => {
  const res = await apiFetch(`/housekeeping/statuses${buildQuery(params)}`);
  return res.json();
};

// ------------------------------------------------------------- Transitions

const post = async <TBody>(
  path: string,
  body: TBody,
  accountId?: number
): Promise<HousekeepingRequestDto> => {
  const res = await apiFetch(`${path}${buildQuery({ accountId })}`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  return res.json();
};

/** Raises a job by hand — a stayover clean, or a re-raise after a cancellation
 * left the room dirty. A departing guest's job is opened by check-out itself
 * and never comes through here. 409 if the room already has a live job. */
export const createHousekeepingRequest = (
  payload: CreateHousekeepingRequestDto,
  accountId?: number
) => post("/housekeeping", payload, accountId);

export const assignHousekeepingRequest = (
  id: number,
  payload: AssignHousekeepingRequestDto,
  accountId?: number
) => post(`/housekeeping/${id}/assign`, payload, accountId);

export const startHousekeepingRequest = (
  id: number,
  payload: StartHousekeepingRequestDto,
  accountId?: number
) => post(`/housekeeping/${id}/start`, payload, accountId);

/** REWORK_REQUIRED → IN_PROGRESS. The same server call as `start`; a separate
 * route because the button says "Start rework" and the history should read
 * that way. */
export const startHousekeepingRework = (
  id: number,
  payload: StartHousekeepingRequestDto,
  accountId?: number
) => post(`/housekeeping/${id}/start-rework`, payload, accountId);

export const pauseHousekeepingRequest = (
  id: number,
  payload: PauseHousekeepingRequestDto,
  accountId?: number
) => post(`/housekeeping/${id}/pause`, payload, accountId);

export const resumeHousekeepingRequest = (
  id: number,
  payload: ResumeHousekeepingRequestDto,
  accountId?: number
) => post(`/housekeeping/${id}/resume`, payload, accountId);

/** IN_PROGRESS → COMPLETED. Does NOT release the room — that is
 * `approveHousekeepingInspection`. */
export const completeHousekeepingRequest = (
  id: number,
  payload: CompleteHousekeepingRequestDto,
  accountId?: number
) => post(`/housekeeping/${id}/complete`, payload, accountId);

/** The inspection PASS — the only call in this API that moves a room from
 * HOUSEKEEPING back to AVAILABLE. */
export const approveHousekeepingInspection = (
  id: number,
  payload: ApproveInspectionDto,
  accountId?: number
) => post(`/housekeeping/${id}/approve-inspection`, payload, accountId);

/** The inspection FAIL. COMPLETED → REWORK_REQUIRED; the room stays put. */
export const markHousekeepingReworkRequired = (
  id: number,
  payload: MarkReworkRequiredDto,
  accountId?: number
) => post(`/housekeeping/${id}/rework`, payload, accountId);

/** Calls the job off. Does NOT release the room — a job abandoned
 * half-cleaned leaves a dirty room. */
export const cancelHousekeepingRequest = (
  id: number,
  payload: CancelHousekeepingRequestDto,
  accountId?: number
) => post(`/housekeeping/${id}/cancel`, payload, accountId);
